import { createContext, createEffect, createSignal, useContext, type Accessor, type ParentComponent } from "solid-js";
import { createStore } from "solid-js/store";
import { batch } from "solid-js";
import {
  getAllVoyageOverrides,
  getVoyageDetails,
  getVoyageOverrides,
  putVoyageOverrides,
  type VoyageStopOverride,
} from "../../shared/api/endpoints";
import type { ItineraryStop, ScheduleVoyage, Voyage, VoyageDetails } from "../../shared/api/types";
import { createAsyncCache } from "../../shared/util/async-cache";
import { rebuildOverrideList, sameIdentity, stampIdentity } from "./override-identity";

// Resolved adjacent-voyage state used by the panel + popup. null while no
// adjacent ref exists, "loading" while in flight, "no-ref" if the fetch
// failed, the parsed VoyageDetails on success.
export type AdjacentState = VoyageDetails | "loading" | "no-ref";

export interface VoyageDetailsStore {
  // Per-tab voyage selection — switching tabs restores whichever voyage that
  // tab last had open (or closes the panel if none).
  tabSelections: {
    calendar: ScheduleVoyage | null;
    vessels: Voyage | null;
  };
  // Async-fetched detail for the currently-open voyage.
  currentDetails: VoyageDetails | null;
  // Error message from the most recent failed loadDetailsFor (cleared on next
  // load attempt or successful fetch). Surfaced in the StatusSection column.
  currentError: string | null;
  // Modal popup overlay state — opened by double-click on a voyage; this
  // is the only details surface (the bottom DetailsPanel was removed).
  popupOpen: boolean;
  // Popup-internal voyage override. When set, the popup shows this voyage
  // instead of the tab's selection — used by Prev/Next inside the popup
  // so navigation between voyages stays in the popup and doesn't perturb
  // what's selected on the underlying calendar/vessels view. The modal's
  // own auto-load effect picks this up and fetches the matching details.
  // Cleared whenever the popup closes so a fresh open always tracks the tab.
  popupVoyageRef: { vesselCode: string; voyageNo: string; placeholder: Voyage } | null;
  // Per-voyage stevedore + volume overrides, keyed by `${vesselCode}/${voyageNo}`.
  // Sparse: only stops that have at least one non-null override field appear
  // in the array (matches the backend's drop-empty behaviour). Populated by
  // the auto-load effect that fires whenever currentDetails changes.
  overridesByVoyage: Record<string, VoyageStopOverride[]>;
}

export interface VoyageDetailsContextValue {
  store: VoyageDetailsStore;
  setSelection: {
    (tab: "calendar", voyage: ScheduleVoyage | null): void;
    (tab: "vessels", voyage: Voyage | null): void;
    (tab: "calendar" | "vessels", voyage: ScheduleVoyage | Voyage | null): void;
  };
  setDetails: (details: VoyageDetails | null) => void;
  setPopupOpen: (open: boolean) => void;
  // Switch the popup to another voyage without touching the tab selection.
  // Looks up the target's VoyageDetails in the prev/next adjacents to build
  // a rich Voyage-shaped placeholder so the popup shows real fields
  // instantly instead of "—" during the details fetch.
  navigatePopupVoyage: (vesselCode: string, voyageNo: string) => void;
  loadDetailsFor: (vesselCode: string, voyageNo: string) => Promise<void>;
  isLoadingDetails: () => boolean;
  // Synchronous read-through to the in-memory cache of fetched voyage
  // details. Used by the Port Calendar's tooltip template, which runs on
  // hover — no need for reactive subscription since each hover re-reads.
  getCachedDetails: (vesselCode: string, voyageNo: string) => VoyageDetails | null;
  // Walk the cache looking for a (vesselCode, voyageReference) match.
  // The schedule API may not return a peer voyage's entry at a given
  // terminal, but its full voyage-details might still be cached
  // (warmed via the prev/next chain or the prewarm-all-vessel effect)
  // — the tooltip uses this to surface peer-voyage cargo when the
  // schedule-level lookup misses.
  findCachedByRef: (vesselCode: string, voyageReference: string) => VoyageDetails | null;
  // Warm the cache for a voyage without touching `currentDetails`. Used
  // by the calendar's hover handler to pre-load details before the user
  // clicks — cache hit → instant noop, miss → fetch + cacheSet. Returns
  // the resolved details (or null on failure) so the caller can refresh
  // the tooltip if it's still hovering the same item.
  warmDetails: (vesselCode: string, voyageNo: string) => Promise<VoyageDetails | null>;
  // Reactive count of voyage details currently in the in-memory cache.
  // Used by the status bar's "N details loaded" idle suffix.
  cachedDetailsCount: Accessor<number>;
  // Warm `overridesByVoyage[key]` without requiring a modal open or a
  // `currentDetails` swap. The calendar tooltip's prewarm path uses this
  // so per-segment overrides land in the tooltip on first hover rather
  // than waiting for the user to open the voyage-details modal.
  warmOverrides: (vesselCode: string, voyageNo: string) => Promise<void>;
  // Bulk-warm: pull every override row in the DB in one round-trip,
  // grouped into overridesByVoyage. Dedup on first call so re-mounts
  // are no-ops; subsequent edits use saveStopOverride's per-voyage
  // path. Used by the calendar to populate stripes without N parallel
  // per-voyage GETs.
  warmAllOverrides: () => Promise<void>;
  // Reactive count of (vesselCode, voyageNo) entries with overrides
  // resolved into the store. Bumped on every successful or empty warm.
  // Drives the calendar tooltip's "redraw on arriving data" effect so a
  // freshly-warmed override re-renders the open tooltip.
  overridesLoadedCount: Accessor<number>;
  // Adjacent-voyage refs (Prev / Next of currentDetails). Auto-fetched
  // by the provider whenever currentDetails changes; both the bottom
  // panel and the popup subscribe so they stay in sync without each
  // running their own fetch.
  prevAdj: Accessor<AdjacentState | null>;
  nextAdj: Accessor<AdjacentState | null>;
  // Read overrides for a voyage from the cache — empty array if none
  // loaded yet or the voyage genuinely has no overrides. Reactive via
  // store access.
  getOverrides: (vesselCode: string, voyageNo: string) => VoyageStopOverride[];
  // Replace the override row for one stop, then PUT the whole voyage's
  // overrides back to the backend (the API is whole-list-replace). Cache
  // is updated optimistically with the backend's echo on success.
  saveStopOverride: (
    vesselCode: string,
    voyageNo: string,
    row: VoyageStopOverride,
    stop: ItineraryStop,
    stops: readonly ItineraryStop[],
  ) => Promise<void>;
  // Remove one override row by identity (falls back to stopOrder +
  // dummySource for legacy rows with no stamped identity — see
  // override-identity.ts's sameIdentity). Used by the orphan callout's
  // Delete action.
  deleteOverride: (vesselCode: string, voyageNo: string, row: VoyageStopOverride) => Promise<void>;
  // Re-bind an orphaned override onto a live stop: delete the orphan row,
  // then save under the chosen stop's freshly-stamped identity. Used by
  // the orphan callout's Reattach action.
  reattachOverride: (
    vesselCode: string,
    voyageNo: string,
    row: VoyageStopOverride,
    stop: ItineraryStop,
    stops: readonly ItineraryStop[],
  ) => Promise<void>;
}

const VoyageDetailsContext = createContext<VoyageDetailsContextValue>();

export const VoyageDetailsProvider: ParentComponent = (props) => {
  const [store, setStore] = createStore<VoyageDetailsStore>({
    tabSelections: { calendar: null, vessels: null },
    currentDetails: null,
    currentError: null,
    popupOpen: false,
    popupVoyageRef: null,
    overridesByVoyage: {},
  });

  function voyageFromDetails(d: VoyageDetails): Voyage {
    return {
      vesselName: d.vesselName,
      vesselCode: d.vesselCode,
      voyageNo: d.voyageNo,
      voyageReference: d.voyageReference,
      voyageStatus: d.voyageStatus,
      tradeArea: d.tradeArea,
      lob: d.lob,
      oprType: d.oprType,
      commenceDate: d.commenceDateLocal,
      completeDate: d.completeDateLocal,
      endOfVoyageDistance: d.endOfVoyageDistance,
      totalSeaDays: d.totalSeaDays,
      totalPortDays: d.totalPortDays,
    };
  }

  function overrideKey(vesselCode: string, voyageNo: string): string {
    return `${vesselCode}/${voyageNo}`;
  }

  // LRU-capped voyage-details cache with in-flight dedup and write-through
  // fetch (see shared/util/async-cache.ts). The status bar's "N details
  // loaded" suffix tracks size via the onSize callback. The cap keeps a
  // long pan/scroll session from accumulating an unbounded number of full
  // VoyageDetails objects in memory.
  const [cachedDetailsCount, setCachedDetailsCount] = createSignal(0);
  const [overridesLoadedCount, setOverridesLoadedCount] = createSignal(0);

  // Fetch overrides for (vesselCode, voyageNo) and stash them in the
  // store. Deduped per voyage so concurrent calls fan in. On failure
  // we still write `[]` so subsequent hovers don't keep re-fetching a
  // 500 in a loop; the user opening the modal will get a fresh attempt
  // because the failure path leaves the modal's auto-load effect to
  // retry separately (it routes through this same function and the
  // `[]` slot triggers a re-fetch only if cleared elsewhere).
  async function warmOverrides(vesselCode: string, voyageNo: string): Promise<void> {
    const key = overrideKey(vesselCode, voyageNo);
    if (store.overridesByVoyage[key]) return;
    try {
      const rows = await getVoyageOverrides(vesselCode, voyageNo);
      setStore("overridesByVoyage", key, rows);
    } catch {
      setStore("overridesByVoyage", key, []);
    }
    setOverridesLoadedCount((n) => n + 1);
  }

  // Dedup the bulk fetch — only fire it once per session. Subsequent
  // updates ride the saveStopOverride path.
  let warmAllPromise: Promise<void> | null = null;
  function warmAllOverrides(): Promise<void> {
    if (warmAllPromise) return warmAllPromise;
    warmAllPromise = (async () => {
      try {
        const rows = await getAllVoyageOverrides();
        const grouped: Record<string, VoyageStopOverride[]> = {};
        for (const r of rows) {
          const key = overrideKey(r.vesselCode, r.voyageNo);
          (grouped[key] ??= []).push(r);
        }
        batch(() => {
          for (const [key, list] of Object.entries(grouped)) {
            setStore("overridesByVoyage", key, list);
          }
        });
      } catch {
        // Bulk failed — leave the cache empty; per-voyage warmOverrides
        // will lazy-load on hover as a fallback.
      }
      setOverridesLoadedCount((n) => n + 1);
    })();
    return warmAllPromise;
  }
  // Key shape: `${vesselCode}/${voyageNo}`. The fetcher decodes by splitting
  // on the first `/` — voyageNos can themselves contain `/` (rare), so we
  // anchor on indexOf rather than `split`.
  const detailsCache = createAsyncCache<VoyageDetails>({
    maxSize: 250,
    onSize: setCachedDetailsCount,
    fetcher: (key) => {
      const slash = key.indexOf("/");
      return getVoyageDetails(key.slice(0, slash), key.slice(slash + 1));
    },
  });
  function detailsKey(vesselCode: string, voyageNo: string): string {
    return `${vesselCode}/${voyageNo}`;
  }
  let requestSeq = 0;
  const [loading, setLoading] = createSignal(false);

  // Adjacent voyages (Prev/Next of currentDetails). Owned by the provider
  // so the popup, the calendar's connection-line follow-up navigation, and
  // any future surface that wants to render Prev/Next links all share one
  // fetch sequence.
  const [prevAdj, setPrevAdj] = createSignal<AdjacentState | null>(null);
  const [nextAdj, setNextAdj] = createSignal<AdjacentState | null>(null);
  let adjReqSeq = 0;
  createEffect(() => {
    const d = store.currentDetails;
    if (!d) {
      setPrevAdj(null);
      setNextAdj(null);
      return;
    }
    const myReq = ++adjReqSeq;
    // Synchronous cache check — if the adjacent is already loaded, skip
    // the "loading" placeholder and hand it over immediately. Otherwise
    // mark loading and let getOrFetchDetails do the network round-trip.
    if (d.previousVoyageNo) {
      const prevKey = detailsKey(d.vesselCode, d.previousVoyageNo);
      const prevCached = detailsCache.get(prevKey);
      if (prevCached) {
        setPrevAdj(prevCached);
      } else {
        setPrevAdj("loading");
        void detailsCache.getOrFetch(prevKey)
          .then((adj) => {
            if (myReq !== adjReqSeq) return;
            setPrevAdj(adj);
          })
          .catch(() => { if (myReq === adjReqSeq) setPrevAdj("no-ref"); });
      }
    } else {
      setPrevAdj(null);
    }
    if (d.nextVoyageNo) {
      const nextKey = detailsKey(d.vesselCode, d.nextVoyageNo);
      const nextCached = detailsCache.get(nextKey);
      if (nextCached) {
        setNextAdj(nextCached);
      } else {
        setNextAdj("loading");
        void detailsCache.getOrFetch(nextKey)
          .then((adj) => {
            if (myReq !== adjReqSeq) return;
            setNextAdj(adj);
          })
          .catch(() => { if (myReq === adjReqSeq) setNextAdj("no-ref"); });
      }
    } else {
      setNextAdj(null);
    }
  });

  const value: VoyageDetailsContextValue = {
    store,
    // Spread-copy the incoming voyage so Solid's setStore reconciliation can't
    // reach back through the proxy and mutate the original entry inside the
    // calendar / vessels store. Without this, selecting voyage B mutates A's
    // fields in place — its id-derivation source data — so A vanishes from
    // the calendar's items() memo on the next selection. Both ScheduleVoyage
    // and Voyage are flat (only ScheduleVoyage.overlapVoyageRefs is nested
    // and arrays-of-strings get replaced wholesale), so a single-level spread
    // is safe.
    setSelection: (tab, voyage) =>
      setStore("tabSelections", tab, voyage ? { ...voyage } : null),
    setDetails: (details) => setStore("currentDetails", details),
    setPopupOpen: (open) => {
      // Closing the popup always drops the navigation override so the
      // next double-click opens straight onto the freshly-clicked voyage
      // rather than wherever the user left off via Prev/Next.
      if (!open) setStore("popupVoyageRef", null);
      setStore("popupOpen", open);
    },
    navigatePopupVoyage: (vesselCode, voyageNo) => {
      const targetKey = `${vesselCode}/${voyageNo}`;
      // Prefer a fully-loaded adjacent — it gives us a real placeholder
      // (vesselName, route, dates, …) so the popup shows real fields
      // while the fresh details fetch resolves through the cache.
      const candidates: VoyageDetails[] = [];
      const p = prevAdj();
      if (typeof p === "object" && p !== null) candidates.push(p);
      const n = nextAdj();
      if (typeof n === "object" && n !== null) candidates.push(n);
      const cur = store.currentDetails;
      if (cur) candidates.push(cur);
      const match = candidates.find((c) => `${c.vesselCode}/${c.voyageNo}` === targetKey);
      // Fallback: synthesise a thin placeholder when no adjacent has been
      // cached yet (e.g. the user navigated immediately on popup open).
      const placeholder: Voyage = match ? voyageFromDetails(match) : {
        vesselName: "",
        vesselCode,
        voyageNo,
        voyageReference: "",
        voyageStatus: "",
        tradeArea: "",
        lob: "",
        oprType: "" as Voyage["oprType"],
        commenceDate: "",
        completeDate: "",
        endOfVoyageDistance: 0,
        totalSeaDays: 0,
        totalPortDays: 0,
      };
      setStore("popupVoyageRef", { vesselCode, voyageNo, placeholder });
    },
    loadDetailsFor: async (vesselCode, voyageNo) => {
      const key = detailsKey(vesselCode, voyageNo);
      const cached = detailsCache.get(key);
      if (cached) {
        setStore({ currentDetails: cached, currentError: null });
        return;
      }
      const myReq = ++requestSeq;
      // Clear stale details immediately so the StatusSection / ItinerarySection
      // columns drop the previous voyage's data and show "Loading…" while the
      // new fetch is in flight. Without this, switching selections leaves the
      // previous voyage's details visible until the new request resolves —
      // and forever if the new request fails.
      setStore({ currentDetails: null, currentError: null });
      setLoading(true);
      try {
        // Route through detailsCache.getOrFetch so a hover-warm that's
        // already in flight for the same voyage gets reused (single
        // network request, no race). Cache write is handled inside.
        const data = await detailsCache.getOrFetch(key);
        if (myReq !== requestSeq) return;
        setStore({ currentDetails: data, currentError: null });
      } catch (err) {
        if (myReq !== requestSeq) return;
        // Mirror OLD app behaviour: surface the error in the panel so the
        // user knows the load failed rather than seeing perpetual "Loading…"
        // (or worse, stale data from the previous selection).
        setStore({ currentDetails: null, currentError: (err as Error).message });
      } finally {
        if (myReq === requestSeq) setLoading(false);
      }
    },
    isLoadingDetails: () => loading(),
    getCachedDetails: (vesselCode, voyageNo) =>
      detailsCache.get(detailsKey(vesselCode, voyageNo)) ?? null,
    findCachedByRef: (vesselCode, voyageReference) => {
      for (const v of detailsCache.values()) {
        if (v.vesselCode === vesselCode && v.voyageReference === voyageReference) {
          return v;
        }
      }
      return null;
    },
    warmDetails: async (vesselCode, voyageNo) => {
      try {
        return await detailsCache.getOrFetch(detailsKey(vesselCode, voyageNo));
      } catch {
        return null;
      }
    },
    cachedDetailsCount,
    warmOverrides,
    warmAllOverrides,
    overridesLoadedCount,
    prevAdj,
    nextAdj,
    getOverrides: (vesselCode, voyageNo) =>
      store.overridesByVoyage[overrideKey(vesselCode, voyageNo)] ?? [],
    saveStopOverride: async (vesselCode, voyageNo, row, stop, stops) => {
      const key = overrideKey(vesselCode, voyageNo);
      // Stamp content identity AND the storage key from the live stop so
      // the row survives an upstream itinerary re-cut that shuffles
      // stopOrder — see override-identity.ts for the resolution/dedup
      // rules this mirrors. Also what makes reattach work: the caller
      // passes the orphan row + the target stop, and stopOrder must
      // follow the target, not the orphan's stale position.
      const stamped = stampIdentity(row, stop, stops);
      const next = rebuildOverrideList(store.overridesByVoyage[key] ?? [], stamped);
      const echoed = await putVoyageOverrides(vesselCode, voyageNo, next);
      setStore("overridesByVoyage", key, echoed);
      // Tick the loaded counter so consumers (e.g. the calendar stripe
      // renderer) re-fire their effects on save, not just on first fetch.
      setOverridesLoadedCount((n) => n + 1);
    },
    deleteOverride: async (vesselCode, voyageNo, row) => {
      const key = overrideKey(vesselCode, voyageNo);
      const next = (store.overridesByVoyage[key] ?? []).filter((r) => !sameIdentity(r, row));
      const echoed = await putVoyageOverrides(vesselCode, voyageNo, next);
      setStore("overridesByVoyage", key, echoed);
      setOverridesLoadedCount((n) => n + 1);
    },
    // Orphan's stamped identity no longer matches any live stop, so a
    // plain saveStopOverride (which dedups by identity) would leave the
    // orphan row behind alongside the newly-bound one — delete first,
    // then save under the target stop's freshly-stamped identity.
    reattachOverride: async (vesselCode, voyageNo, row, stop, stops) => {
      await value.deleteOverride(vesselCode, voyageNo, row);
      await value.saveStopOverride(vesselCode, voyageNo, row, stop, stops);
    },
  };

  // Auto-load overrides alongside details. Cached per voyage so re-opening
  // the popup is instant — overrides are user-edited and small, so we keep
  // them in store rather than re-fetching on every selection. The calendar
  // tooltip's prewarm path goes through warmOverrides directly, but the
  // modal still needs this fallback in case details land via a path that
  // didn't prewarm (vessels-tab double-click, Prev/Next inside the modal).
  createEffect(() => {
    const d = store.currentDetails;
    if (!d) return;
    void warmOverrides(d.vesselCode, d.voyageNo);
  });

  return (
    <VoyageDetailsContext.Provider value={value}>
      {props.children}
    </VoyageDetailsContext.Provider>
  );
};

export function useVoyageDetails(): VoyageDetailsContextValue {
  const ctx = useContext(VoyageDetailsContext);
  if (!ctx) throw new Error("useVoyageDetails must be used inside VoyageDetailsProvider");
  return ctx;
}
